'use client';

import React from 'react';
import { usePathname } from 'next/navigation';
import { Bell } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useMondayAuth } from '@/contexts/MondayAuthContext';
import { useHeaderStore } from '@/stores/headerStore';
import { HelpMenu } from './HelpMenu';

export interface TopBarProps {
  className?: string;
}

// Map routes to default page titles (used when no page-specific title is set)
const PAGE_TITLES: Record<string, string> = {
  '/': 'Dashboard',
  '/csv-import': 'CSV Import',
  '/bulk-operations': 'Bulk Operations',
  '/recurring-patterns': 'Recurring Tasks',
  '/activity': 'Activity',
  '/settings': 'Settings',
  '/upgrade': 'Upgrade',
};

const PAGE_SUBTITLES: Record<string, string> = {
  '/recurring-patterns': 'Automatically create tasks on a schedule',
};

export function TopBar({ className }: TopBarProps) {
  const pathname = usePathname();
  const { context } = useMondayAuth();

  // Get page-injected header content from store
  const { pageTitle: injectedTitle, pageContext, headerActions } = useHeaderStore();

  // Use injected title if available, otherwise fall back to route-based title
  const pageTitle = injectedTitle || PAGE_TITLES[pathname || '/'] || 'TaskLoops';
  const pageSubtitle = PAGE_SUBTITLES[pathname || '/'];

  return (
    <header
      className={cn(
        'h-14 bg-[var(--surface-topbar)] border-b border-[var(--border-default)] px-5',
        'flex items-center justify-between',
        className
      )}
    >
      {/* Left: Page Title + Subtitle + Context Badge */}
      <div className="flex items-center gap-3">
        <h1 className="text-lg font-semibold font-heading text-[var(--text-heading)]">{pageTitle}</h1>
        {pageSubtitle && (
          <span className="text-sm text-[var(--text-secondary)]">{pageSubtitle}</span>
        )}
        {pageContext && (
          <span className="text-xs px-2.5 py-1 rounded-md bg-[var(--surface-hover)] text-[var(--text-secondary)] font-medium">
            {pageContext}
          </span>
        )}
      </div>

      {/* Right: Page Actions + Global Actions */}
      <div className="flex items-center gap-2">
        {/* Page-injected actions render here */}
        {headerActions}

        {/* Separator when there are page actions */}
        {headerActions && (
          <div className="w-px h-6 bg-[var(--border-default)] mx-1" />
        )}

        {/* Notifications (placeholder for future) */}
        <button
          className="relative p-2 rounded-lg text-[var(--text-secondary)] hover:bg-[var(--surface-hover)] hover:text-[var(--text-body)] transition-colors"
          aria-label="Notifications"
        >
          <Bell className="w-5 h-5" />
        </button>

        {/* Help */}
        <HelpMenu />

      </div>
    </header>
  );
}
